import React, { useState, useEffect } from 'react';
import { Paper, Typography, Stack, Box, Button, Alert, CircularProgress } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Cancel, Edit } from '@mui/icons-material';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import axios from '../../api/axios';
import { useNotification } from '../../utils/useNotification';

function MultipleChoiceComponent({ example, onAnswerSubmitted, isTeacher = false, embedded = false }) {
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { showPointsNotification, NotificationComponent } = useNotification();
  
  const getCorrectIndex = (correctAnswer) => {
    if (!correctAnswer) return -1;
    return correctAnswer.charCodeAt(0) - 'A'.charCodeAt(0);
  };
  
  const correctIndex = getCorrectIndex(example.correctAnswer || example.correct_answer);
  
  // 获取已提交的答案（复习/嵌入模式下不预填，由 AI 解答）
  useEffect(() => {
    if (embedded) return;
    const fetchSubmittedAnswer = async () => {
      try {
        const questionId = `notebook_${example.id}`;
        const response = await axios.get(`/submissions/question/${questionId}`);
        const submissions = response.data || [];
        if (submissions.length > 0) {
          const latestSubmission = submissions[0];
          try {
            const answerData = JSON.parse(latestSubmission.code);
            if (answerData.answer) {
              // 将答案字母转换为索引
              const answerLetter = answerData.answer.toUpperCase();
              const answerIndex = answerLetter.charCodeAt(0) - 'A'.charCodeAt(0);
              setSelected(answerIndex);
              setHasSubmitted(true);
              setSubmissionResult({
                status: latestSubmission.status,
                isCorrect: latestSubmission.status === 'passed',
                score: latestSubmission.score
              });
            }
          } catch (e) {
            // 如果解析失败，尝试直接使用
            console.error('Error parsing submission:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching submitted answer:', error);
      }
    };
    
    if (example.id) {
      fetchSubmittedAnswer();
    }
  }, [example.id, embedded]);
  
  const handleSubmit = async () => {
    if (selected === null) {
      alert('请先选择一个答案');
      return;
    }
    
    setSubmitting(true);
    setSubmissionResult(null);
    
    try {
      // Convert index to letter (A, B, C, D)
      const answerLetter = String.fromCharCode(65 + selected);
      const response = await axios.post(`/notebook-examples/${example.id}/submit`, {
        answer: answerLetter
      });
      
      const submission = response.data.submission;
      // 复习/嵌入模式：提交后不保留作答状态，界面恢复为「未答过题」
      if (embedded) {
        setSelected(null);
        setHasSubmitted(false);
        setSubmissionResult(null);
        setShowAnswer(false);
      } else {
        setSubmissionResult(submission);
        setShowAnswer(true);
        setHasSubmitted(true);
        setIsEditing(false);
      }
      
      // 复习/嵌入模式不弹积分提示；非复习且通过时弹
      if (!embedded && submission.status === 'passed' && submission.score > 0 && !isTeacher) {
        showPointsNotification(submission.score);
      }
      
      // Notify parent（复习页会据此请求 AI 反馈，传入作答结果）
      if (onAnswerSubmitted) {
        if (embedded) {
          onAnswerSubmitted(answerLetter, { isCorrect: submission.status === 'passed', score: submission.score });
        } else {
          onAnswerSubmitted(answerLetter);
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setSubmissionResult({
        status: 'error',
        isCorrect: false,
        error: error.response?.data?.error || '提交失败，请重试'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Paper
      elevation={embedded ? 0 : undefined}
      sx={{ 
        p: 5, 
        ...(embedded && { pl: 0, pr: 0 }),
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%', 
        maxWidth: '900px', 
        mx: 'auto',
        boxSizing: 'border-box',
        ...(embedded && { bgcolor: 'transparent' }),
      }}
    >
      <Box sx={{ mb: 3 }}>
        <MarkdownRenderer content={example.question} />
      </Box>
      
      <Stack spacing={1} sx={{ mb: 3 }}>
        {example.options?.map((option, idx) => {
          const isCorrect = idx === correctIndex;
          const isSelectedOption = selected === idx;
          const isWrongSelected = !embedded && submissionResult && !submissionResult.isCorrect && isSelectedOption;
          const showCorrectWrong = !embedded && showAnswer;
          return (
            <Box
              key={idx}
              sx={{
                p: 2,
                border: 2,
                borderColor: showCorrectWrong && isCorrect ? 'success.main' : 
                            isWrongSelected ? 'error.main' :
                            isSelectedOption ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: showCorrectWrong && isCorrect ? 'success.light' : 
                         isWrongSelected ? 'error.light' :
                         isSelectedOption ? 'primary.light' : 'transparent',
                cursor: (showCorrectWrong && !isEditing) ? 'default' : 'pointer',
                transition: 'all 0.2s',
                '&:hover': showCorrectWrong ? {} : { 
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => (!showCorrectWrong || isEditing) && setSelected(idx)}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {showCorrectWrong && isCorrect ? (
                  <CheckCircle color="success" sx={{ mt: 0.5, flexShrink: 0 }} />
                ) : isWrongSelected ? (
                  <Cancel color="error" sx={{ mt: 0.5, flexShrink: 0 }} />
                ) : (
                  <RadioButtonUnchecked sx={{ mt: 0.5, color: isSelectedOption ? 'primary.main' : 'inherit', flexShrink: 0 }} />
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <MarkdownRenderer content={option?.trim() ?? ''} />
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      
      {!embedded && submissionResult && (
        <Alert 
          severity={submissionResult.isCorrect ? 'success' : 'error'} 
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            {submissionResult.isCorrect ? '✓ 回答正确！' : '✗ 回答错误'}
            {submissionResult.error && ` - ${submissionResult.error}`}
          </Typography>
        </Alert>
      )}
      
      {!embedded && showAnswer && example.explanation && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box component="div" variant="body2">
            <strong>解释：</strong>
            <MarkdownRenderer content={example.explanation} />
          </Box>
        </Alert>
      )}
      
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {(!hasSubmitted || isEditing || embedded) ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={selected === null || submitting}
            sx={{ minWidth: 120 }}
          >
            {submitting ? <CircularProgress size={20} /> : hasSubmitted ? '重新提交' : '提交答案'}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                setIsEditing(true);
                setShowAnswer(false);
              }}
            >
              修改答案
            </Button>
            <Button
              variant={showAnswer ? 'outlined' : 'contained'}
              onClick={() => setShowAnswer(!showAnswer)}
              disabled={!isTeacher && !hasSubmitted && !isEditing}
            >
              {showAnswer ? '隐藏答案' : '显示答案'}
            </Button>
          </>
        )}
      </Stack>
      
      <NotificationComponent />
    </Paper>
  );
}

export default MultipleChoiceComponent;
